import React from 'react';
import { LinkContext } from '../LinkContext';
import { TypographyToken, Intent, useFullTheme, Responsive, ResponsiveMarginProps } from '~/design';
import { Breakpoint } from '~/design/breakpoint';
import { useResponsive } from '~/utils';
import { CSS, useResponsiveMarginStyle } from '~/utils/styling';

export type TextContrast = 'high' | 'mid' | 'low' | 'inverted';

export type TextAlign = 'start' | 'end' | 'center';

export interface InternalTextProps extends ResponsiveMarginProps {
  'aria-activedescendant'?: string;
  'aria-autocomplete'?: string;
  'aria-controls'?: string;
  'aria-expanded'?: string;
  'aria-labelledby'?: string;

  /** Text alignment */
  align?: TextAlign | Responsive<TextAlign>;

  /** The underlying DOM node to render */
  as?: 'span' | 'div' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'legend' | 'li';

  /** The text content */
  children?: React.ReactNode;

  /** The color to use for the text */
  color?: 'inherit';

  /** Contrast color of the text, defaulted to high */
  contrast?: TextContrast;

  /** The identifier of the text. */
  id?: string;

  /** The color to use for the text ("regular" shade) */
  intent?: Intent;

  /** The maximum number of lines to show.*/
  lineClamp?: number | Responsive<number>;

  /** Dictates how the text overflows. */
  overflow?: 'ellipsis' | 'nowrap' | 'normal';

  /** The size/weight/line-height to use */
  variant?: TypographyToken | Responsive<TypographyToken>;

  /** The way long words break when the element runs out of room */
  wordBreak?: 'word';
}

const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'] as const;

export const InternalText = React.forwardRef<
  | HTMLSpanElement
  | HTMLDivElement
  | HTMLParagraphElement
  | HTMLHeadingElement
  | HTMLLegendElement
  | HTMLLIElement,
  InternalTextProps
>(function InternalText(
  {
    align = 'start',
    as: Tag = 'div',
    children,
    color,
    contrast = 'high',
    id,
    intent = 'secondary',
    lineClamp,
    m,
    mb,
    ml,
    mr,
    mt,
    mx,
    my,
    overflow,
    variant = 'bodyM',
    wordBreak,
  },
  ref,
) {
  const { typography } = useFullTheme();
  const colorUtil = useTextColor(intent, contrast);
  const responsive = useResponsive();
  const linkContext = React.useContext(LinkContext);

  const marginStyle = useResponsiveMarginStyle({
    m,
    mb,
    ml,
    mr,
    mt,
    mx,
    my,
  });

  const variantStyle = React.useMemo(() => {
    const isVariantResponsive = typeof variant !== 'string';
    if (!isVariantResponsive) {
      return typography[variant];
    }

    // variants are inherently responsive. For example, `bodyL` looks like this:
    //
    //    fontFamily: "Open Sans"
    //    fontSize: {xs: '1rem', sm: '1.125rem', md: '1.125rem', lg: '1.125rem', xl: '1.25rem'}
    //    fontWeight: 400
    //    letterSpacing: {xs: 'normal', sm: 'normal', md: 'normal', lg: 'normal', xl: 'normal'}
    //    lineHeight: {xs: '1.5rem', sm: '2rem', md: '2rem', lg: '2rem', xl: '2rem'}
    //
    // So we'll want an object that looks just like this, except it merges the values of the breakpoints per variant.
    // For example, { xs: bodyS, md: bodyL } would use the font size from bodyS for xs/sm, then the font size from
    // bodyL for md and up.

    const result: CSS = {};

    for (let i = 0; i < breakpoints.length; i++) {
      const breakpoint = breakpoints[i];
      const breakpointToUse: Breakpoint | undefined = breakpoints
        .slice(0, i + 1)
        .reverse()
        .find((b): TypographyToken | undefined => variant[b]);

      if (!breakpointToUse) {
        continue;
      }

      const variantToUse = variant[breakpointToUse];
      if (!variantToUse) {
        continue;
      }

      const variantAttrs = typography[variantToUse];
      // cssProperty will be one of e.g. `fontFamily`, `fontWeight`...
      for (const cssProperty in variantAttrs) {
        // @ts-expect-error I'm too stupid to figure out how to type this
        const cssPropertyBreakpoints = result[cssProperty] ?? {};

        const value = variantAttrs[cssProperty as keyof typeof variantAttrs];
        if (typeof value !== 'object') {
          cssPropertyBreakpoints[breakpoint] = value;
        } else {
          cssPropertyBreakpoints[breakpoint] = value[breakpoint];
        }

        // @ts-expect-error I'm too stupid to figure out how to type this
        result[cssProperty] = cssPropertyBreakpoints;
      }
    }
    return result;
  }, [typography, variant]);

  return (
    <Tag
      css={[
        responsive.css(variantStyle),
        responsive.css({ textAlign: align }),
        {
          color: color ?? colorUtil(),
          textTransform: 'none',
          whiteSpace: overflow ? (overflow === 'normal' ? 'normal' : 'nowrap') : undefined,

          // Setting position to relative prevents the children from causing parents to be scrollable
          // See: https://applyboard.atlassian.net/browse/AWA-5846?focusedCommentId=1104622
          // and: https://applyboard.slack.com/archives/C02F4DWK99U/p1739274628666379
          position: overflow === 'ellipsis' ? 'relative' : undefined,

          wordBreak: wordBreak === 'word' ? 'break-word' : undefined,

          // No component in our design system should have built-in whitespace.
          // built-in whitespace is the root of all evil
          // p and h1-h6 have margin by default in browsers
          margin: 0,
          paddingInlineStart: 0,
          paddingInlineEnd: 0,

          // When tag is `legend`, we need it to be display:contents so it plays nicely
          // with `fieldset`s that have display:flex
          // ref: https://developer.mozilla.org/en-US/docs/Web/CSS/display#:~:text=boxes%20at%20all.-,contents,-These%20elements%20don%27t
          // ref: https://drafts.csswg.org/css-display/#unbox-html:~:text=display%3A%20none.-,legend,-Per%20HTML%2C%20a
          display: Tag === 'legend' ? 'contents' : undefined,
        },
        marginStyle,
        lineClamp && {
          display: '-webkit-box',
          overflow: 'hidden',
          WebkitBoxOrient: 'vertical',
          ...responsive.css({
            WebkitLineClamp: lineClamp,
          }),
        },
        !!linkContext &&
          linkContext.saliency !== 'low' && {
            color: 'inherit',
          },
        overflow === 'ellipsis' && {
          overflowX: 'hidden',
          textOverflow: 'ellipsis',
        },
      ]}
      id={id}
      // @ts-expect-error The type of `ref` depends on the value of `as`, not sure how to check that
      ref={ref}
    >
      {children}
    </Tag>
  );
});

export function useTextColor(intent: Intent = 'secondary', contrast?: TextContrast) {
  const { colors } = useFullTheme();

  return () => {
    switch (contrast) {
      case 'inverted':
        return colors.textOnAction;
      case 'low':
        return colors.textInactive;
      case 'high':
        return {
          magic: colors.textMagicStark,
          negative: colors.textNegativeStark,
          positive: colors.textPositiveStark,
          primary: colors.textPrimaryStark,
          secondary: colors.textSecondaryStark,
          warning: colors.textWarningStark,
        }[intent];
      default:
        return {
          magic: colors.textMagic,
          negative: colors.textNegative,
          positive: colors.textPositive,
          primary: colors.textPrimary,
          secondary: colors.textSecondary,
          warning: colors.textWarning,
        }[intent];
    }
  };
}
